import { useState } from "react";
import { Plus, Pencil, Trash2, Target } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { fmtMoney, fmtPct, uid } from "@/lib/finance";
import { PageHeader, SectionCard, Money, EmptyState, AddButton, ProgressBar, StatusPill } from "@/components/shared";
import { FormModal, TextField, NumberField, SelectField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const GOAL_TYPES = [
  { value: "savings", label: "Savings" },
  { value: "debt", label: "Debt payoff" },
  { value: "investment", label: "Investment" },
  { value: "purchase", label: "Purchase" },
  { value: "other", label: "Other" },
];
const PRIORITIES = [{ value: "high", label: "High" }, { value: "med", label: "Medium" }, { value: "low", label: "Low" }];
const STATUSES = [{ value: "active", label: "Active" }, { value: "paused", label: "Paused" }, { value: "completed", label: "Completed" }];

export default function Goals() {
  const { state, update, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const goals = state.goals || [];
  const openNew = () => { setForm({ id: uid(), type: "savings", name: "", target: "", current: "", priority: "med", status: "active", monthly: "", targetDate: "" }); setOpen(true); };
  const save = () => {
    if (!form.name) return toast.error("Name is required");
    const g = { ...form, target: Number(form.target || 0), current: Number(form.current || 0), monthly: Number(form.monthly || 0) };
    update((prev) => {
      const exists = (prev.goals || []).some((x) => x.id === g.id);
      return { ...prev, goals: exists ? prev.goals.map((x) => (x.id === g.id ? g : x)) : [...(prev.goals || []), g] };
    }, `Saved goal: ${g.name}`);
    setOpen(false); toast.success("Goal saved");
  };
  const remove = (id) => update((prev) => ({ ...prev, goals: prev.goals.filter((x) => x.id !== id) }), "Deleted goal");

  const totalTarget = goals.reduce((s, g) => s + Number(g.target || 0), 0);
  const totalCurrent = goals.reduce((s, g) => s + Number(g.current || 0), 0);

  return (
    <div>
      <PageHeader title="Goals" subtitle="Set targets and watch your progress compound." testid="goals-header"
        actions={<AddButton onClick={openNew} testid="add-goal-button"><Plus className="h-4 w-4 mr-1.5" />Add goal</AddButton>} />

      {goals.length ? (
        <>
          <SectionCard className="mb-4" title="Overall progress" right={<span className="text-sm text-[var(--text-muted)]">{fmtMoney(totalCurrent)} / {fmtMoney(totalTarget)}</span>}>
            <ProgressBar value={totalCurrent} max={totalTarget} tone="gold" />
          </SectionCard>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goals.map((g) => {
              const pct = g.target ? Math.min(100, (g.current / g.target) * 100) : 0;
              return (
                <SectionCard key={g.id} testid={`goal-card-${g.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2"><h3 className="font-serif-display text-lg text-[var(--text)]">{g.name}</h3><StatusPill status={g.status} /></div>
                      <div className="text-[11px] text-[var(--text-muted)] capitalize mt-0.5">{g.type} · {g.priority} priority</div>
                    </div>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm({ ...g }); setOpen(true); }} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)]" onClick={() => remove(g.id)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-serif-display text-xl tabular-nums">{fmtMoney(g.current)}</span>
                      <span className="text-sm text-[var(--text-muted)]">of {fmtMoney(g.target)} · {fmtPct(pct, 0)}</span>
                    </div>
                    <ProgressBar value={g.current} max={g.target} tone={g.status === "completed" ? "green" : "gold"} />
                    {g.monthly > 0 && <div className="text-[11px] text-[var(--text-faint)] mt-2">Contributing {fmtMoney(g.monthly)}/mo{g.targetDate ? ` · target ${g.targetDate}` : ""}</div>}
                  </div>
                </SectionCard>
              );
            })}
          </div>
        </>
      ) : <SectionCard><EmptyState icon={Target} title="No goals yet" message="Create your first financial goal and start tracking progress." action={<AddButton onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Add goal</AddButton>} /></SectionCard>}

      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={goals.some((x) => x.id === form.id) ? "Edit goal" : "Add goal"} onSubmit={save} testid="goal-modal">
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} full testid="goal-name-input" />
          <SelectField label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={GOAL_TYPES} testid="goal-type-select" />
          <SelectField label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={PRIORITIES} />
          <NumberField label="Target amount" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} testid="goal-target-input" />
          <NumberField label="Current amount" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} testid="goal-current-input" />
          <NumberField label="Monthly contribution" value={form.monthly} onChange={(e) => setForm({ ...form, monthly: e.target.value })} />
          <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} />
          <TextField label="Target date" type="date" value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} full />
        </FormModal>
      )}
    </div>
  );
}
